import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'

import type { UserInfo, LoginResponse } from '@/types/auth'

// 用户状态接口
interface UserState {
	// 用户信息
	userInfo: UserInfo | null
	// 登录状态
	isLoggedIn: boolean
	// 加载状态
	isLoading: boolean
	// 错误信息
	error: string | null
	// 设置用户信息
	setUserInfo: (userInfo: UserInfo | null) => void
	// 设置登录状态
	setIsLoggedIn: (isLoggedIn: boolean) => void
	// 设置加载状态
	setIsLoading: (isLoading: boolean) => void
	// 设置错误信息
	setError: (error: string | null) => void
	// 登录
	login: (userInfo: UserInfo) => void
	// 登出
	logout: () => void
}

// 创建用户状态管理store
export const useUserStore = create(
	immer<UserState>(set => ({
		// 初始状态
		userInfo: null,
		isLoggedIn: false,
		isLoading: false,
		error: null,

		// 设置用户信息
		setUserInfo: userInfo =>
			set(state => {
				state.userInfo = userInfo
			}),

		// 设置登录状态
		setIsLoggedIn: isLoggedIn =>
			set(state => {
				state.isLoggedIn = isLoggedIn
			}),

		// 设置加载状态
		setIsLoading: isLoading =>
			set(state => {
				state.isLoading = isLoading
			}),

		// 设置错误信息
		setError: error =>
			set(state => {
				state.error = error
			}),

		// 登录 - 支持直接传入UserInfo或LoginResponse格式
		login: (loginData: UserInfo | LoginResponse) =>
			set(state => {
				// 处理嵌套的登录响应结构
				const userInfo: UserInfo = 'user_info' in loginData
					? {
						...loginData.user_info,
						token: loginData.token
					}
					: loginData

				state.userInfo = userInfo
				state.isLoggedIn = true
				state.error = null
				// 存储到localStorage
				if (typeof window !== 'undefined') {
					localStorage.setItem('userInfo', JSON.stringify(userInfo))
					localStorage.setItem('token', userInfo.token || '')
				}
			}),

		// 登出
		logout: () =>
			set(state => {
				state.userInfo = null
				state.isLoggedIn = false
				// 清除localStorage
				if (typeof window !== 'undefined') {
					localStorage.removeItem('userInfo')
					localStorage.removeItem('token')
					// 清除菜单数据缓存
					localStorage.removeItem('menuData')
					// 清除cookie中的登录信息
					document.cookie.split(';').forEach(cookie => {
						const name = cookie.trim().split('=')[0]
						// 清除可能包含认证信息的cookie
						if (name.includes('token') || name.includes('user') || name.includes('auth')) {
							document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
						}
					})
				}
			})
	}))
)

// 初始化用户状态
export const initUserStore = (): void => {
	// 确保只在客户端执行
	if (typeof window === 'undefined') {
		return
	}

	// 从localStorage获取用户信息
	const userInfoStr = localStorage.getItem('userInfo')
	if (userInfoStr) {
		try {
			const userInfo = JSON.parse(userInfoStr) as UserInfo
			useUserStore.getState().login(userInfo)
		} catch (error) {
			console.error('解析用户信息失败:', error)
			// 清除无效的用户信息
			localStorage.removeItem('userInfo')
			localStorage.removeItem('token')
		}
	}
}

// 获取当前登录用户信息
export const getCurrentUser = (): UserInfo | null => {
	return useUserStore.getState().userInfo
}

// 检查用户是否已登录
export const isAuthenticated = (): boolean => {
	return useUserStore.getState().isLoggedIn
}

// 获取当前用户角色
export const getCurrentUserRole = (): UserInfo['role'] | null => {
	const userInfo = useUserStore.getState().userInfo
	return userInfo ? userInfo.role : null
}

// 获取当前用户ID
export const getCurrentUserId = (): number | null => {
	const userInfo = useUserStore.getState().userInfo
	return userInfo ? userInfo.user_id : null
}

// 获取当前用户名
export const getCurrentUsername = (): string | null => {
	const userInfo = useUserStore.getState().userInfo
	return userInfo ? userInfo.user_name : null
}

// 获取当前用户昵称
export const getCurrentNickName = (): string | null => {
	const userInfo = useUserStore.getState().userInfo
	return userInfo ? userInfo.nick_name : null
}

// 获取当前用户邮箱
export const getCurrentUserEmail = (): string | null => {
	const userInfo = useUserStore.getState().userInfo
	return userInfo ? userInfo.email : null
}
